import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { memo, Suspense, useCallback, useEffect, useMemo, } from 'react';
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Typography, } from '@mui/material';
import { BackAwareDialog } from 'shared/components/dialogs/backAwareDialog';
import { ArrowBackRounded, Close } from '@mui/icons-material';
import { isSmallUI } from 'shared/app/lib/choose-ui-type';
import { ErrorBoundary } from 'shared/components/error-boundary';
import * as classnames from 'classnames';
import { MobileDialogAppBar } from './settings/mobile-app-bar';
import { FadeTransition, SlideUpTransition, } from 'console/react-elements/SlideUpTransition';
import { on_back_button, pop_back_item, push_back_item } from 'console/utils/back-button';
import { isMobileApp } from 'webui/utils/version';
import { useNavigateBack, useNavigateRoot } from 'shared/hooks/router';
import { styled } from '@mui/material/styles';
import { exitApp } from 'console/entities/app';
import { useKeybind } from 'console/dialogs/model/use-keybind';
import { DeviceStateIndicator } from 'shared/components/indicators/device-state';
/**
 * @function
 * Универсальный диалог
 * На пк - диалог, на мобильнике - экран
 * @param props
 * @return ReactElement
 */
export const UniversalDialog = memo(function UniversalDialog(props) {
    const { open, onClose, children, title, device_time, closeButtonTitle, footer, contentWithZeroPadding, fullScreenAppBar, noIPhoneBottomPadding, className, content_sx, additionalHeader, router, disable_backbutton = false, breadcrumbs, onBack, iconState, ...rest } = props;
    const full_screen = isSmallUI();
    const DialogComponent = useMemo(() => (router || disable_backbutton ? Dialog : BackAwareDialog), [router, disable_backbutton]);
    const styled_dialog = useCallback((theme) => ({
        ...theme.typography.body1,
        '& [class^="MuiDialogTitle"]': {
            pb: 0,
            pt: 1,
            '& h6': {
                color: theme.palette.mode == 'dark'
                    ? theme.palette.primary.light
                    : theme.palette.primary.dark,
            },
        },
        ...(!full_screen && {
            '& > * > [class^="MuiPaper"]': {
                borderTopWidth: '3px',
                borderTopStyle: 'solid',
                borderTopColor: theme.palette.primary.light,
            },
        }),
    }), [full_screen]);
    const dialog_content_sx = useMemo(() => ({
        p: contentWithZeroPadding ? 0 : undefined,
        ...content_sx,
    }), [contentWithZeroPadding, content_sx]);
    return (_jsxs(DialogComponent, { open: open, onClose: onClose, className: className, fullScreen: full_screen, TransitionComponent: full_screen ? SlideUpTransition : FadeTransition, sx: styled_dialog, ...rest, children: [full_screen ? (fullScreenAppBar ?? (_jsx(MobileDialogAppBar, { active_tab_title: null, active_tab_id: router ? 'tab-id' : null, default_tab_title: title, onClose: onClose, onBack: onBack }))) : (_jsx(DialogTitle, { children: _jsxs(Box, { display: "flex", alignItems: "center", justifyContent: "space-between", children: [router && (_jsx(IconButton, { onClick: onBack, edge: "start", size: "large", children: _jsx(ArrowBackRounded, {}) })), _jsx(Box, { children: _jsxs(Box, { display: "flex", flexDirection: "column", alignItems: "center", children: [_jsxs(Box, { display: "flex", justifyContent: "center", children: [iconState && (_jsx(Box, { mr: 1, display: "flex", alignItems: "center", children: _jsx(DeviceStateIndicator, { state: iconState, fontSize: "1.1rem" }) })), _jsx(Typography, { variant: "h6", color: "primary", children: title }), device_time] }), breadcrumbs] }) }), additionalHeader != null && additionalHeader, _jsx(IconButton, { onClick: onClose, edge: "end", size: "large", children: _jsx(Close, {}) })] }) })), _jsx(DialogContent, { className: classnames({
                    DialogMobileFooter: footer == null && !closeButtonTitle && !noIPhoneBottomPadding,
                }), sx: dialog_content_sx, children: _jsx(ErrorBoundary, { children: children }) }), (closeButtonTitle || footer != null) && (_jsx(DialogActions, { className: classnames({
                    DialogMobileFooter: !noIPhoneBottomPadding,
                }), children: footer ? (footer) : (_jsx(Button, { variant: "contained", color: "primary", onClick: onClose, children: closeButtonTitle })) }))] }));
});
export const UniversalDialogForRouter = memo(function UniversalDialogForRouter(props) {
    const onClose = useNavigateRoot();
    const onBack = useNavigateBack();
    useEffect(() => {
        if (isMobileApp()) {
            document.removeEventListener('backbutton', on_back_button);
            document.addEventListener('backbutton', onBack);
            pop_back_item(exitApp, false);
        }
        return () => {
            if (isMobileApp()) {
                document.removeEventListener('backbutton', onBack);
                document.addEventListener('backbutton', on_back_button);
                setTimeout(() => {
                    push_back_item(exitApp);
                }, 500);
            }
        };
    }, []);
    useKeybind({ onBack });
    return (_jsx(UniversalDialog, { router: true, open: true, onClose: onClose, onBack: onBack, scroll: "paper", ...props }));
});
export const StyledUniversalDialogForRouter = styled(UniversalDialogForRouter, {
    shouldForwardProp: (prop) => prop !== 'is_medium_ui',
})(({ theme, is_medium_ui }) => ({
    ...(is_medium_ui && {
        '& .MuiDialog-container': {
            overflow: 'hidden',
        },
        '& .MuiDialog-paper': {
            margin: `${theme.spacing(1)} auto`,
            height: '100%',
            maxWidth: '1000px',
            maxHeight: `calc(100vh - ${theme.spacing(2)})`,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
        },
    }),
}));
export const SettingsDialogWithRouter = memo(function SettingsDialogWithRouter(props) {
    const { children, ...other_props } = props;
    return (_jsx(StyledUniversalDialogForRouter, { ...other_props, children: _jsx(Box, { flexGrow: 1, overflow: "hidden", height: "100%", boxSizing: "border-box", children: _jsx(Suspense, { fallback: _jsx(CircularProgress, { size: 50, color: "primary", sx: {
                        zIndex: 401,
                        position: 'absolute',
                        left: 'calc(50% - 25px)',
                        top: 'calc(50% - 25px)',
                    } }), children: children }) }) }));
});
